<template>
    <div class="index-message">
        <div class="message-icon" @click="handleOpen">
            <el-badge :value="getTotalUnread()" :max="99" class="item">
                <el-tooltip effect="dark" :content="getTooltipContent()" placement="bottom">
                    <i class="el-icon-message-solid message-bell" :class="{ 'has-unread': getTotalUnread() > 0 }"/>
                </el-tooltip>
            </el-badge>
        </div>

        <el-drawer
            title=""
            :modal="modal"
            :visible.sync="drawer"
            :direction="direction"
            :size="size"
            append-to-body
            class="message-drawer">

            <!-- 自定义标题栏 -->
            <div slot="title" class="drawer-header">
                <div class="header-title">
                    <div class="header-icon-wrapper">
                        <i class="el-icon-message-solid"></i>
                    </div>
                    <span>消息中心</span>
                </div>
                <div class="header-stats">
                    <el-tag type="danger" size="small" v-if="getTotalUnread() > 0" class="stats-tag">
                        <i class="el-icon-warning-outline"></i>
                        {{ getTotalUnread() }}条未阅
                    </el-tag>
                    <el-tag type="success" size="small" v-else class="stats-tag">
                        <i class="el-icon-circle-check"></i>
                        全部已阅
                    </el-tag>
                </div>
            </div>

            <!-- 消息类型切换 -->
            <div class="message-tabs">
                <el-tabs v-model="activeTab" @tab-click="handleTabClick" class="custom-tabs">
                    <el-tab-pane label="站内消息" name="message">
                        <span slot="label" class="tab-label">
                            <i class="el-icon-chat-dot-square"></i>
                            <span>站内消息</span>
                            <el-badge :value="unreadMessage" :max="99" class="tab-badge"
                                      v-if="unreadMessage > 0"></el-badge>
                        </span>
                    </el-tab-pane>
                    <el-tab-pane label="系统公告" name="notice">
                        <span slot="label" class="tab-label">
                            <i class="el-icon-bell"></i>
                            <span>系统公告</span>
                            <el-badge :value="unreadNotice" :max="99" class="tab-badge"
                                      v-if="unreadNotice > 0"></el-badge>
                        </span>
                    </el-tab-pane>
                </el-tabs>
            </div>

            <!-- 操作栏 -->
            <div class="message-operating">
                <el-row>
                    <el-col :span="14">
                        <el-dropdown @command="handleCommand" class="filter-dropdown">
                            <span class="el-dropdown-link">
                                <i :class="getFilterIcon()"></i>
                                <span>{{ getFilterText() }}</span>
                                <i class="el-icon-arrow-down el-icon--right"/>
                </span>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="all">
                                    <i class="el-icon-bell"></i>
                                    <span>全部消息</span>
                                </el-dropdown-item>
                                <el-dropdown-item command="unread">
                                    <i class="el-icon-chat-dot-square"/>
                                    <span>未阅消息</span>
                                </el-dropdown-item>
                                <el-dropdown-item command="read" v-if="activeTab === 'message'">
                                    <i class="el-icon-check"/>
                                    <span>已阅消息</span>
                                </el-dropdown-item>
                                <el-dropdown-item command="urgent" v-if="activeTab === 'notice'">
                                    <i class="el-icon-warning-outline"/>
                                    <span>紧急公告</span>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </el-col>
                    <el-col :span="10" class="text-right">
                        <el-button
                            type="text"
                            size="small"
                            @click="doReadAll"
                            :disabled="getTotalUnread() === 0"
                            class="action-btn">
                            <i class="el-icon-check"></i>
                            <span>全部已阅</span>
                        </el-button>
                        <el-button
                            type="text"
                            size="small"
                            @click="refreshData"
                            class="action-btn">
                            <i class="el-icon-refresh" :class="{ 'rotating': loading }"></i>
                            <span>刷新</span>
                        </el-button>
                    </el-col>
                </el-row>
            </div>

            <!-- 消息内容区域 -->
            <div class="message-content">
                <!-- 站内消息列表 -->
                <div v-if="activeTab === 'message'" class="message-list-container">
                    <!-- 空状态优化 -->
                    <div v-if="messageList.length === 0 && !loading" class="empty-state">
                        <div class="empty-icon">
                            <i class="el-icon-chat-dot-square"></i>
                        </div>
                        <div class="empty-text">
                            <p class="empty-title">暂无站内消息</p>
                            <p class="empty-desc">您的消息将会在这里显示</p>
                        </div>
                        <el-button type="text" @click="refreshData" class="empty-action">
                            <i class="el-icon-refresh"></i>
                            刷新试试
                        </el-button>
                    </div>

                    <ul v-else class="message-list" v-infinite-scroll="loadOther"
                        :infinite-scroll-disabled="loading || !hasMore">
                        <li
                            v-for="(item, index) in messageList"
                            :key="'message' + index"
                            @click="readMessage(item, index)"
                            :class="getMessageItemClass(item)">

                            <div class="message-header">
                                <div class="message-subject">
                                    <div class="subject-icon">
                                        <i :class="getMessageIcon(item)" class="message-type-icon"></i>
                                    </div>
                                    <span class="subject-text">{{ item.messageSubject }}</span>
                                </div>
                                <div class="message-status">
                                    <el-tag v-if="item.readStatus === 0" type="danger" size="mini" class="status-tag">
                                        <i class="el-icon-warning-outline"></i>
                                        未阅
                                    </el-tag>
                                    <!--<span class="message-time">{{ formatTime(item.createTime) }}</span>-->
                                    <span class="message-time">{{ item.sendTime }}</span>
                                </div>
                            </div>

                            <div class="message-body">
                                <div class="notice-content" v-if="item.templateType === 'MARKDOWN'"
                                     v-html="item.messageContent"></div>
                                <div class="notice-content" v-else-if="item.templateType === 'HTML'">
                                    <el-button type="text" size="mini" @click.stop="showHtmlDialog(item)"
                                               class="preview-btn">
                                        <i class="el-icon-view"></i>
                                        查看详情
                                    </el-button>
                                </div>
                                <div class="notice-content" v-else>
                                    <p>{{ getContentPreview(item.messageContent) }}</p>
                                </div>
                            </div>

                            <div class="message-actions">
                                <el-button type="text" size="mini" @click.stop="goLink(item, index)" class="read-btn">
                                    <i class="el-icon-link"></i>
                                    跳转
                                </el-button>
                                <el-button type="text" size="mini" @click.stop="doRead(item, index)" class="read-btn"
                                           v-if="item.readStatus === 0">
                                    <i class="el-icon-check"></i>
                                    标为已阅
                                </el-button>
                            </div>
                        </li>
                    </ul>

                    <div v-if="loading" class="loading-more">
                        <i class="el-icon-loading"></i>
                        <span>加载中...</span>
                    </div>
                </div>

                <!-- 系统公告列表 -->
                <div v-if="activeTab === 'notice'" class="notice-list-container">
                    <!-- 空状态优化 -->
                    <div v-if="noticeList.length === 0 && !loading" class="empty-state">
                        <div class="empty-icon">
                            <i class="el-icon-bell"></i>
                        </div>
                        <div class="empty-text">
                            <p class="empty-title">暂无系统公告</p>
                            <p class="empty-desc">系统公告将会在这里显示</p>
                        </div>
                        <el-button type="text" @click="refreshData" class="empty-action">
                            <i class="el-icon-refresh"></i>
                            刷新试试
                        </el-button>
                    </div>

                    <ul v-else class="notice-list">
                        <li
                            v-for="(item, index) in noticeList"
                            :key="'notice' + index"
                            @click="readNotice(item, index)"
                            :class="getNoticeItemClass(item)">

                            <div class="notice-header">
                                <div class="notice-title">
                                    <div class="title-icon">
                                        <i :class="getNoticeIcon(item.category)" class="notice-type-icon"></i>
                                    </div>
                                    <span class="title-text">{{ item.title || item.messageSubject }}</span>
                                    <el-tag
                                        v-if="item.category === 4"
                                        type="danger"
                                        size="mini"
                                        class="urgent-tag">
                                        <i class="el-icon-warning"></i>
                                        紧急
                                    </el-tag>
                                </div>
                                <div class="notice-meta">
                                    <el-tag v-if="item.readStatus !== 1" type="danger" size="mini" class="status-tag">
                                        <i class="el-icon-warning-outline"></i>
                                        未阅
                                    </el-tag>
                                    <el-tag :type="getNoticeCategoryType(item.category)" size="mini"
                                            class="category-tag">
                                        {{ getNoticeCategoryName(item.category) }}
                                    </el-tag>
                                    <span class="notice-time">{{
                                            formatTime(item.releaseTime || item.createTime)
                                        }}</span>
                                </div>
                            </div>

                            <div class="notice-content">
                                <p>{{ getContentPreview(item.messageContent) }}</p>
                            </div>

                            <div class="notice-footer">
                                <span class="notice-date">
                                    <i class="el-icon-time"></i>
                                    有效期至: {{ item.endTime ? item.endTime.split(' ')[0] : '永久' }}
                                </span>
                                <div class="notice-actions">
                                    <el-button type="text" size="mini" @click.stop="previewNotice(item)"
                                               class="preview-btn">
                                        <i class="el-icon-view"></i>
                                        查看详情
                                    </el-button>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- 公告详情弹窗 -->
            <el-dialog
                :visible.sync="noticeDetailVisible"
                :title="currentNotice.messageSubject"
                width="60%"
                :modal-append-to-body="false"
                class="notice-detail-dialog">
                <div v-if="currentNotice" class="notice-detail">
                    <div class="notice-detail-header">
                        <div class="notice-meta-info">
                            <el-tag :type="getNoticeCategoryType(currentNotice.category)" size="small">
                                {{ getNoticeCategoryName(currentNotice.category) }}
                            </el-tag>
                            <span class="publish-time">发布时间: {{
                                    currentNotice.releaseTime || currentNotice.createTime
                                }}</span>
                            <span class="expire-time">截止时间: {{ currentNotice.endTime || '永久有效' }}</span>
                        </div>
                    </div>
                    <div class="notice-detail-content" v-html="formatNoticeContent(currentNotice.messageContent)"></div>
                </div>
            </el-dialog>
            <!-- 站内通知html格式弹窗 -->
            <el-dialog
                :visible.sync="htmlDialogVisible"
                :title="currentMessage.messageSubject"
                width="40%"
                :show-close="false"
                :fullscreen="false"
                :append-to-body="true"
                class="notice-detail-dialog">
                <div v-html="currentMessage.messageContent" class="notice-detail-content"></div>
            </el-dialog>
        </el-drawer>
    </div>
</template>

<script>

import {getMessageIcon, getRoutePath} from '@/const/andon/messageConst'
import {NOTICE_CATEGORY_ICON, NOTICE_CATEGORY_MAP} from '@/const/messageConst'

export default {
    name: 'indexMessage',
    title: '消息中心',
    props: {},
    components: {},
    methods: {
        // 开启抽屉
        handleOpen () {
            this.size = this.drawerSize
            this.drawer = true
            this.refreshData()
        },

        // 刷新数据
        refreshData () {
            if (this.activeTab === 'message') {
                this.current = 1
                this.messageList = []
                this.hasMore = true
                this.loadList()
            } else {
                this.loadNoticeList()
            }
        },

        // 切换标签页
        handleTabClick (tab) {
            this.activeTab = tab.name
            this.status = null
            this.refreshData()
        },

        // 处理搜索条件
        handleCommand (command) {
            if (command === 'all') {
                this.status = null
            } else if (command === 'unread') {
                this.status = 1
            } else if (command === 'read') {
                this.status = 2
            } else if (command === 'urgent') {
                this.urgentOnly = true
            }

            this.refreshData()
        },

        // 加载更多
        loadOther () {
            if (this.activeTab === 'message' && this.hasMore && !this.loading) {
                this.current += 1
                this.loadList()
            }
        },

        // 获取站内消息列表
        loadList () {
            if (this.loading) return
            this.loading = true

            this.etao.requests.get({
                url: '/etao-resource/unified-message/business/list',
                data: {
                    page: this.current,
                    size: this.pageSize,
                    isRead: this.status === 1 ? false : this.status === 2 ? true : null,
                    channelType: 'WEB'
                },
                successMessage: false,
                success: res => {
                    if (res.data) {
                        this.messageList.push(...res.data)
                        this.hasMore = res.data.length === this.pageSize
                    } else {
                        this.hasMore = false
                    }
                },
                complete: () => {
                    this.loading = false
                }
            })
        },

        // 获取公告列表
        loadNoticeList () {
            this.loading = true
            const params = {
                page: 1,
                size: 20,
                isRead: this.status === 1 ? false : this.status === 2 ? true : null
            }

            this.etao.requests.get({
                url: '/etao-resource/unified-message/notice/list',
                data: params,
                successMessage: false,
                success: res => {
                    this.noticeList = res.data || []
                },
                complete: () => {
                    this.loading = false
                    this.urgentOnly = false
                }
            })
        },

        // 获取未阅消息数量（仅业务消息）
        getMineUnread () {
            this.etao.requests.get({
                url: '/etao-resource/unified-message/business/unread-count',
                successMessage: false,
                success: res => {
                    this.unreadMessage = res.data || 0
                }
            })
        },

        // 获取未阅公告数量
        getUnreadNoticeCount () {
            this.etao.requests.get({
                url: '/etao-resource/unified-message/notice/unread-count',
                successMessage: false,
                success: res => {
                    this.unreadNotice = res.data || 0
                }
            })
        },

        // 读取消息
        readMessage (item, index) {
            if (item.readStatus === 0) {
                this.doRead(item, index)
            }
            // 关闭抽屉
            this.drawer = false
            // 跳转到消息详情页
            const routePath = getRoutePath(item.templateCode)
            if (routePath) {
                this.$router.push({
                    path: routePath,
                    query: {id: item.businessKey}
                })
            }
        },

        // 查看公告
        readNotice (item, index) {
            this.currentNotice = item
            this.noticeDetailVisible = true
            // 标记公告为已阅
            this.markNoticeAsRead(item)
        },

        // 预览公告
        previewNotice (item) {
            this.currentNotice = item
            this.noticeDetailVisible = true
            this.markNoticeAsRead(item)
        },

        // 标记公告为已阅
        markNoticeAsRead (item) {
            this.etao.requests.post({
                url: '/etao-resource/unified-message/notice/mark-read',
                data: {
                    logId: item.id
                },
                successMessage: false,
                success: () => {
                    if (this.unreadNotice > 0) {
                        this.unreadNotice -= 1
                        this.loadNoticeList()
                    }
                }
            })
        },
        goLink (item, index) {
            const urlRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
            if (item && item.remark && urlRegex.test(item.remark)) {
                try {
                    // 从完整URL中提取路径部分
                    const url = new URL(item.remark)
                    const pathPart = url.pathname
                    const query = {}
                    url.searchParams.forEach((value, key) => {
                        query[key] = value
                    })
                    // 使用路由跳转到提取的路径
                    this.$router.push({path: pathPart, query})
                    // 从路由映射中查找对应的菜单项
                    const routeMap = this.$store.getters['etao/routeMap']
                    if (routeMap && routeMap[pathPart]) {
                        // 更新页面状态
                        this.$store.commit('etao/setPage', routeMap[pathPart])
                    }

                    this.drawer = false
                } catch (error) {
                    console.error('URL解析错误:', error)
                    this.$message.error('链接解析失败')
                }
            } else {
                this.$message.error('链接无效')
            }
        },
        // 标记为已阅
        doRead (item, index) {
            this.etao.requests.post({
                url: '/etao-resource/unified-message/mark-read',
                data: {
                    logId: item.id
                },
                successMessage: false,
                success: res => {
                    this.unreadMessage -= 1
                    this.messageList[index].status = 2
                    this.messageList[index].readStatus = 1
                    this.$set(this.messageList, index, this.messageList[index])
                }
            })
        },

        // 标记所有未阅消息为已阅
        doReadAll () {
            const hasUnreadMessages = this.unreadMessage > 0
            const hasUnreadNotices = this.unreadNotice > 0

            if (!hasUnreadMessages && !hasUnreadNotices) {
                this.etao.message.toast('没有未阅消息')
                return
            }

            const promises = []
            console.log(111)
            if (hasUnreadMessages) {
                // 标记所有业务消息为已阅 - 需要分页获取所有未阅消息然后逐一标记
                promises.push(
                    this.etao.requests.post({
                        url: '/etao-resource/unified-message/mark-all-read',
                        data: {
                            sourceType: 'BUSINESS_MESSAGE'
                        },
                        successMessage: false
                    })
                )
            }

            if (hasUnreadNotices) {
                promises.push(
                    this.etao.requests.post({
                        url: '/etao-resource/unified-message/mark-all-read-notice',
                        data: {
                            sourceType: 'SYSTEM_NOTICE'
                        },
                        successMessage: false
                    })
                )
            }

            Promise.all(promises).then(() => {
                this.unreadMessage = 0
                this.unreadNotice = 0
                this.messageList.forEach((item, index) => {
                    if (item.readStatus === 0) {
                        item.readStatus = 1
                        this.$set(this.messageList, index, item)
                    }
                })
                this.loadNoticeList()
                this.etao.message.toast('全部消息已标为已阅')

                // 刷新公告列表数据，确保已阅数统计更新
                setTimeout(() => {
                    // 如果在公告管理页面，触发刷新
                    if (this.$route.path && this.$route.path.includes('/message/notice')) {
                        this.$bus && this.$bus.$emit('refreshNoticeList')
                    }
                }, 500)
            }).catch(() => {
                this.etao.message.toast('操作失败，请重试')
            })
        },

        // 获取总未阅数
        getTotalUnread () {
            return this.unreadMessage + this.unreadNotice
        },

        // 获取工具提示内容
        getTooltipContent () {
            const total = this.getTotalUnread()
            if (total === 0) {
                return '暂无未阅消息'
            }
            return `${this.unreadMessage}条未阅消息，${this.unreadNotice}条未阅公告`
        },

        // 获取筛选图标
        getFilterIcon () {
            if (this.status === 1) return 'el-icon-chat-dot-square'
            if (this.status === 2) return 'el-icon-check'
            if (this.urgentOnly) return 'el-icon-warning-outline'
            return 'el-icon-bell'
        },

        // 获取筛选文本
        getFilterText () {
            if (this.status === 1) return '未阅消息'
            if (this.status === 2) return '已阅消息'
            if (this.urgentOnly) return '紧急公告'
            return '全部消息'
        },

        // 获取消息项样式类
        getMessageItemClass (item) {
            return [
                'message-item',
                item.readStatus === 0 ? 'unread' : 'read'
            ].join(' ')
        },

        // 获取公告项样式类
        getNoticeItemClass (item) {
            const classes = ['notice-item']

            // 添加紧急状态样式
            if (item.category === 4) {
                classes.push('urgent')
            } else {
                classes.push('normal')
            }

            // 添加阅读状态样式（使用readStatus字段，0-未阅，1-已阅）
            const isRead = item.readStatus === 1
            if (isRead) {
                classes.push('read')
            } else {
                classes.push('unread')
            }

            return classes.join(' ')
        },

        // 获取消息图标
        getMessageIcon (item) {
            return getMessageIcon(item.templateCode)
        },

        // 获取公告图标
        getNoticeIcon (category) {
            return NOTICE_CATEGORY_ICON[category] || 'el-icon-document'
        },

        // 获取公告类型
        getNoticeCategoryType (category) {
            const typeMap = {
                1: 'info', // 系统公告
                2: 'primary', // 业务通知
                3: 'warning', // 维护公告
                4: 'danger' // 紧急通知
            }
            return typeMap[category] || 'info'
        },

        // 获取公告类型名称
        getNoticeCategoryName (category) {
            return NOTICE_CATEGORY_MAP[category] || '未知类型'
        },

        // 格式化时间
        formatTime (time) {
            if (!time) return ''
            const now = new Date()
            const msgTime = new Date(time)
            const diff = now - msgTime

            if (diff < 60000) { // 1分钟内
                return '刚刚'
            } else if (diff < 3600000) { // 1小时内
                return Math.floor(diff / 60000) + '分钟前'
            } else if (diff < 86400000) { // 24小时内
                return Math.floor(diff / 3600000) + '小时前'
            } else {
                return time.split(' ')[0] // 返回日期部分
            }
        },

        // 获取内容预览
        getContentPreview (content) {
            if (!content) return ''
            return content.length > 100 ? content.substring(0, 100) + '...' : content
        },

        // 格式化公告内容
        formatNoticeContent (content) {
            if (!content) return ''
            return content.replace(/\n/g, '<br>')
        },

        // 刷新消息列表
        refreshMessages () {
            console.log('刷新消息列表...')
            // 刷新当前激活的Tab页数据
            if (this.activeTab === 'message') {
                this.messageList = []
                this.current = 0
                this.hasMore = true
                this.loadList()
            } else if (this.activeTab === 'notice') {
                this.noticeList = []
                this.current = 0
                this.hasMore = true
                this.loadNoticeList()
            }
            // 刷新未阅数量（只需要调用一次）
            this.getMineUnread()
            this.getUnreadNoticeCount()
        },
        showHtmlDialog (item) {
            this.currentMessage = item
            this.$nextTick(() => {
                this.htmlDialogVisible = true
            })
        }
    },
    mounted () {
        const user = this.etao.common.getData('userInfo')
        if (user) {
            // 确保组织信息已加载后再获取未读消息数
            this.$nextTick(() => {
                // 检查当前组织是否已设置
                const curOrg = this.etao.common.getCurOrg()
                if (curOrg) {
                    // 组织信息已存在，直接获取
                    this.getMineUnread()
                    this.getUnreadNoticeCount()
                } else {
                    // 组织信息还未加载，监听变化或稍后重试
                    setTimeout(() => {
                        const retryOrg = this.etao.common.getCurOrg()
                        if (retryOrg) {
                            this.getMineUnread()
                            this.getUnreadNoticeCount()
                        }
                    }, 500)
                }
            })
        }
    },
    data () {
        return {
            // 未阅数量
            unreadMessage: 0,
            unreadNotice: 0,

            // 抽屉开关
            drawer: false,
            // 是否显示遮罩层
            modal: false,
            // 显示方向
            direction: 'rtl',
            // 显示大小 - 优化宽度，支持响应式
            size: '520px',

            // 活动标签页
            activeTab: 'message',

            // 分页参数
            current: 1,
            pageSize: 10,
            hasMore: true,
            loading: false,

            // 筛选条件
            status: null, // 1:未阅，2已阅
            urgentOnly: false,

            // 消息列表
            messageList: [],

            // 公告列表
            noticeList: [],

            // 公告详情
            noticeDetailVisible: false,
            currentNotice: {},
            htmlDialogVisible: false,
            currentMessage: {}
        }
    },
    computed: {
        // 计算抽屉尺寸
        drawerSize () {
            const screenWidth = window.innerWidth || 1920
            if (screenWidth <= 768) {
                return '95%' // 移动端
            } else if (screenWidth <= 1200) {
                return '480px' // 中等屏幕
            } else {
                return '520px' // 大屏幕
            }
        }
    }
}
</script>

<style scoped>
.index-message {
    position: relative;
}

.message-icon {
    cursor: pointer;
    display: inline-block;
    transition: all 0.3s ease;
}

.message-icon:hover {
    transform: scale(1.1);
}

.message-bell {
    font-size: 18px;
    color: #606266;
    transition: all 0.3s ease;
}

.message-bell.has-unread {
    color: #F56C6C;
    animation: bell-shake 2s ease-in-out infinite;
}

@keyframes bell-shake {
    0%, 20%, 40%, 60%, 80%, 100% {
        transform: rotate(0deg);
    }
    10% {
        transform: rotate(10deg);
    }
    30% {
        transform: rotate(-10deg);
    }
    50% {
        transform: rotate(10deg);
    }
    70% {
        transform: rotate(-10deg);
    }
    90% {
        transform: rotate(5deg);
    }
}

.message-drawer {
    --el-drawer-padding-primary: 0;
}

.drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    position: relative;
}

.drawer-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    pointer-events: none;
}

.header-title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    position: relative;
    z-index: 1;
}

.header-icon-wrapper {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
}

.header-title i {
    font-size: 16px;
}

.header-title span {
    color: white;
}

.header-stats {
    font-size: 12px;
    position: relative;
    z-index: 1;
}

.stats-tag {
    border: none;
    background: rgba(255, 255, 255, 0.9);
    color: #333;
    font-weight: 500;
}

.stats-tag i {
    margin-right: 4px;
}

.message-tabs {
    background: #f8f9fa;
    border-bottom: 1px solid #EBEEF5;
    flex-shrink: 0;
    padding: 0 8px;
}

.custom-tabs .el-tabs__header {
    margin: 0;
    padding: 0 12px;
    background: white;
}

.custom-tabs .el-tabs__nav-wrap::after {
    display: none;
}

.custom-tabs .el-tabs__item {
    padding: 0 18px;
    height: 48px;
    line-height: 48px;
    font-size: 14px;
    color: #606266;
    font-weight: 500;
    border-radius: 8px 8px 0 0;
    margin-right: 6px;
    transition: all 0.3s ease;
}

.custom-tabs .el-tabs__item.is-active {
    color: #409EFF;
    background: #f0f9ff;
    border-bottom: 2px solid #409EFF;
}

.tab-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 6px;
}

.tab-label i {
    font-size: 14px;
    flex-shrink: 0;
}

.tab-label span {
    flex-shrink: 0;
}

.tab-badge {
    margin-left: 6px;
}

.message-operating {
    padding: 12px 20px;
    background: #fafbfc;
    border-bottom: 1px solid #EBEEF5;
    flex-shrink: 0;
}

.filter-dropdown .el-dropdown-link {
    cursor: pointer;
    color: #409EFF;
    font-size: 13px;
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 6px;
    transition: all 0.3s ease;
    background: white;
    border: 1px solid #E4E7ED;
}

.filter-dropdown .el-dropdown-link:hover {
    background: #f0f9ff;
    border-color: #409EFF;
}

.filter-dropdown .el-dropdown-link i {
    margin-right: 6px;
}

.action-btn {
    margin-left: 8px;
    padding: 6px 12px;
    border-radius: 6px;
    transition: all 0.3s ease;
    border: 1px solid transparent;
}

.action-btn:hover {
    background: #f0f9ff;
    border-color: #409EFF;
    color: #409EFF;
}

.action-btn:disabled {
    color: #C0C4CC;
    background: #f5f7fa;
    border-color: #E4E7ED;
}

.action-btn i {
    margin-right: 4px;
}

.rotating {
    animation: rotate 1s linear infinite;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.message-content {
    flex: 1;
    padding: 0;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.message-list-container,
.notice-list-container {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    min-height: 0; /* 修复flex布局问题 */
}

/* 空状态样式优化 */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    text-align: center;
    padding: 40px 20px;
}

.empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    box-shadow: 0 4px 12px rgba(144, 202, 249, 0.3);
}

.empty-icon i {
    font-size: 24px;
    color: #64b5f6;
}

.empty-text {
    margin-bottom: 16px;
}

.empty-title {
    font-size: 15px;
    color: #303133;
    margin: 0 0 6px 0;
    font-weight: 500;
}

.empty-desc {
    font-size: 13px;
    color: #909399;
    margin: 0;
    line-height: 1.4;
}

.empty-action {
    color: #409EFF;
    font-size: 13px;
    padding: 8px 16px;
    border: 1px solid #409EFF;
    border-radius: 6px;
    background: transparent;
    transition: all 0.3s ease;
}

.empty-action:hover {
    background: #409EFF;
    color: white;
}

.empty-action i {
    margin-right: 4px;
}

/* 修复抽屉内容高度问题 */
.message-drawer .el-drawer__body {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* 标签页容器高度优化 */
.message-tabs {
    background: #f8f9fa;
    border-bottom: 1px solid #EBEEF5;
    flex-shrink: 0;
}

/* 操作栏高度优化 */
.message-operating {
    padding: 12px 20px;
    background: #fafbfc;
    border-bottom: 1px solid #EBEEF5;
    flex-shrink: 0;
}

/* 消息列表基础样式 */
.message-list,
.notice-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

/* 消息列表项间距优化 */
.message-item,
.notice-item {
    background: white;
    margin-bottom: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    border: 1px solid #E4E7ED;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    position: relative;
    overflow: hidden;
}

/* 左侧指示条效果 */
.message-item::before,
.notice-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: transparent;
    transition: all 0.3s ease;
}

/* 悬停效果 */
.message-item:hover,
.notice-item:hover {
    border-color: #409EFF;
    box-shadow: 0 4px 20px rgba(64, 158, 255, 0.15);
    transform: translateY(-2px);
}

.message-item:hover::before,
.notice-item:hover::before {
    background: #409EFF;
}

/* 未阅消息样式 */
.message-item.unread {
    background: linear-gradient(135deg, #fff5f5 0%, #fefefe 100%);
    border-left: 4px solid #F56C6C;
}

.message-item.unread::before {
    background: #F56C6C;
}

/* 未阅公告样式 */
.notice-item.unread {
    background: linear-gradient(135deg, #f0f9ff 0%, #fefefe 100%);
    border-left: 4px solid #409EFF;
}

.notice-item.unread::before {
    background: #409EFF;
}

/* 未阅且紧急的公告样式（优先级更高） */
.notice-item.unread.urgent {
    background: linear-gradient(135deg, #fff5f5 0%, #fefefe 100%);
    border-left: 4px solid #F56C6C;
}

.notice-item.unread.urgent::before {
    background: #F56C6C;
}

/* 紧急公告样式 */
.notice-item.urgent {
    background: linear-gradient(135deg, #fff5f5 0%, #fefefe 100%);
    border-left: 4px solid #F56C6C;
}

.notice-item.urgent::before {
    background: #F56C6C;
}

/* 消息头部布局优化 */
.message-header,
.notice-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
    gap: 12px;
}

.message-subject,
.notice-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    flex: 1;
    display: flex;
    align-items: center;
    line-height: 1.4;
    min-width: 0; /* 允许文本截断 */
}

.subject-icon,
.title-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #f0f9ff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    flex-shrink: 0;
}

.message-type-icon,
.notice-type-icon {
    font-size: 14px;
    color: #409EFF;
}

.subject-text,
.title-text {
    flex: 1;
    word-break: break-word;
    min-width: 0;
}

/* 消息状态区域优化 */
.message-status,
.notice-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    white-space: nowrap;
}

.status-tag,
.category-tag {
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 500;
}

.status-tag i {
    margin-right: 2px;
    font-size: 10px;
}

.urgent-tag {
    margin-left: 8px;
    background: #fee;
    color: #f56c6c;
    border: 1px solid #fbc4c4;
}

.urgent-tag i {
    margin-right: 2px;
    font-size: 10px;
}

.message-time,
.notice-time {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
}

.message-body,
.notice-content {
    font-size: 13px;
    color: #606266;
    line-height: 1.5;
    margin-bottom: 10px;
}

.message-body p,
.notice-content p {
    margin: 0;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.message-actions,
.notice-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.read-btn,
.preview-btn {
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.read-btn:hover,
.preview-btn:hover {
    background: #f0f9ff;
    color: #409EFF;
}

.notice-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #909399;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #F5F7FA;
}

.notice-date {
    display: flex;
    align-items: center;
}

.notice-date i {
    margin-right: 4px;
    color: #C0C4CC;
}

.loading-more {
    text-align: center;
    padding: 16px;
    color: #909399;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: #f8f9fa;
    margin: 0 16px;
    border-radius: 8px;
}

.text-right {
    text-align: right;
}

.notice-detail {
    padding: 20px 0;
}

.notice-detail-header {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #E4E7ED;
}

.notice-meta-info {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 13px;
    color: #909399;
}

.notice-detail-content {
    font-size: 14px;
    line-height: 1.8;
    color: #303133;
}

/* 滚动条样式 */
.message-list-container::-webkit-scrollbar,
.notice-list-container::-webkit-scrollbar {
    width: 6px;
}

.message-list-container::-webkit-scrollbar-track,
.notice-list-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.message-list-container::-webkit-scrollbar-thumb,
.notice-list-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.message-list-container::-webkit-scrollbar-thumb:hover,
.notice-list-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Dropdown菜单样式优化 */
.el-dropdown-menu {
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border: none;
    padding: 8px 0;
}

.el-dropdown-menu__item {
    padding: 8px 16px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
}

.el-dropdown-menu__item:hover {
    background: #f0f9ff;
    color: #409EFF;
}

.el-dropdown-menu__item i {
    font-size: 14px;
    width: 16px;
}

/* 响应式设计优化 */
@media (max-width: 768px) {
    .drawer-header {
        padding: 12px 16px;
    }

    .message-operating {
        padding: 8px 16px;
    }

    .message-operating .el-row {
        flex-direction: column;
        gap: 8px;
    }

    .message-operating .el-col {
        width: 100%;
    }

    .text-right {
        text-align: left;
    }

    .message-list-container,
    .notice-list-container {
        padding: 12px;
    }

    .message-item,
    .notice-item {
        padding: 12px;
        margin-bottom: 8px;
    }

    .message-header,
    .notice-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .message-status,
    .notice-meta {
        width: 100%;
        justify-content: space-between;
    }

    .empty-state {
        padding: 20px;
        min-height: 250px;
    }

    .empty-icon {
        width: 56px;
        height: 56px;
        margin-bottom: 12px;
    }

    .empty-icon i {
        font-size: 20px;
    }
}

/* 动画增强 */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.message-item,
.notice-item {
    animation: fadeInUp 0.3s ease;
}

/* 标签页动画 */
.custom-tabs .el-tabs__item {
    position: relative;
    overflow: hidden;
}

.custom-tabs .el-tabs__item::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background: #409EFF;
    transition: all 0.3s ease;
    transform: translateX(-50%);
}

.custom-tabs .el-tabs__item.is-active::before {
    width: 100%;
}

.message-status {
    display: flex;
    align-items: center;
    gap: 8px;
}

.message-time {
    color: #999;
    font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/index/indexMessage.vue